import {
    IBorders,
    IRowColStartEnd,
    ITableCellModel,
    ITableCellModelForEdit,
    ITableModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@tencent/tencent-doc-report';
import { objectCompareLite, difference } from '@src/slide/infrastructure/module/independent/object-operation';
import { ExcludeMoreBorderProps } from '@src/slide/domain/ui-model/element-modeles/constant/props';
import { NO_COLOR } from '@src/slide/infrastructure/module/independent/color/constant';
import get from 'lodash/get';
import set from 'lodash/set';
import Border from '.';
import { IBorderInfo, ITableBorderInfo, ITableBorderProperty, ITableBorderStyle } from './border.interface';
import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import { ICTTableCell, ITableDataModel, STSchemeColorVal } from '@tencent/slide-sdk-model';
import {
    changeArrAppendToSetOrModify,
} from '@src/slide/infrastructure/module/independent/object/arr-append-to-set-or-modify';
import {
    TableCellPropertyConverter,
} from '@src/slide/domain/ui-model/converter/node-property/element/table-cell-property-converter';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';

const nullToDefault = (value, defaultValue) => value || defaultValue;
export default class TableBorder extends Border implements ITableBorderProperty {
    private tableModel: ITableModel;
    public constructor(model: ITableModel) {
        super(model);
        this.tableModel = model;
    }

    public getTableBorderInfo(): ITableBorderInfo {
        let mergedBorderInfo: undefined | null = null;
        const style = {
            horizontal: true,
            vertical: true,
            left: true,
            right: true,
            top: true,
            bottom: true,
        };
        if (this.tableModel.selectedCellRanges) {
            // 获取内部水平/垂直线的状态
            this.updateHVState(style);
            this.tableModel.selectedCellRanges?.forEach((cellRange) => {
                const row = cellRange.rowStart;
                const col = cellRange.colStart;
                const shapeModel = this.tableModel.tableItems[row][col];
                const borders = get(shapeModel, 'borders');

                // 计算单元格边框颜色
                if (borders && mergedBorderInfo !== undefined) {
                    const borderKeys = Object.keys(borders);
                    borderKeys.forEach((key) => {
                        const borderInfo = get(borders[key], 'border');
                        // mergedBorderInfo为初始值null，直接赋值
                        if (mergedBorderInfo === null) {
                            mergedBorderInfo = borderInfo;
                        } else {
                            const equal = objectCompareLite(
                                mergedBorderInfo,
                                borderInfo,
                                ExcludeMoreBorderProps as any,
                            );
                            if (!equal) {
                                const res = difference(mergedBorderInfo, borderInfo);
                                Object.keys(res).forEach((deleteKey: string) => {
                                    if (mergedBorderInfo) {
                                        delete mergedBorderInfo[deleteKey];
                                    }
                                });
                                // mergedBorderInfo = undefined;
                            }
                        }
                    });
                }
                // 计算单元格边框样式
                this.caculateCellBorderStyle(cellRange, borders, style);
            });
        }
        const borderStyle = this.getBorderStyle(style);
        return {
            borderStyle,
            borderColor: typeof mergedBorderInfo === 'string' ? { color: mergedBorderInfo } : mergedBorderInfo,
        };
    }

    public updateTableBorderInfo = (tableBorderInfo: IBorderInfo): void => {
        this.tableModel.uploadEditingCellData(false);
        this.modifyBorderInfo(tableBorderInfo);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TABLE_FORMAT,
            action: TDWActionType.TABLE_BORDER_COLOR,
        });
    };

    public clearTableBorderStyle = (): void => {
        const sdkDataV3 = this.tableModel.sdkData as unknown as ITableDataModel;
        sdkDataV3.api.transaction(() => {
            this.tableModel.uploadEditingCellData(false);

            this.tableModel.props.items.forEach((rowInfo, rowIndex) => {
                rowInfo.forEach((col, colIndex) => {
                    const range = { startRowIndex: rowIndex, startColIndex: colIndex, rowSpan: 1, colSpan: 1 };
                    sdkDataV3.api.updateTableCellProperties({
                        lnB: null,
                        lnL: null,
                        lnR: null,
                        lnT: null,
                    }, range);
                });
            });
        });
    };

    public updateTableBorderStyle = (borderStyle: ITableBorderStyle): void => {
        this.tableModel.uploadEditingCellData(false);
        this.modifyBorderStyle(borderStyle);
    };

    /** ***** 获取表格的颜色和边框样式(table-model.ts迁移而来) start ***** */
    /**
     * 获取边框样式
     */
    private getBorderStyle = (style): ITableBorderStyle => {
        const outer = this.allOuterBorderExist(style);
        const inner = this.allInnerBorderExist(style);
        // 所有边框都存在
        const all = outer && inner;
        const none = this.allBorderNotExist(style);
        // 统计得到选中区域的边框样式信息
        const borderStyle = {
            border_none: none,
            border_all: all,
            border_outer: outer,
            border_inner: inner,
            border_horizontal: style.horizontal,
            border_vertical: style.vertical,
            border_top: style.top,
            border_right: style.right,
            border_bottom: style.bottom,
            border_left: style.left,
        };
        return borderStyle;
    };

    /**
     * 所有外边框都存在
     */
    private allOuterBorderExist = (style) => {
        const outer = style.left && style.right && style.top && style.bottom;
        return outer;
    };

    /**
     * 所有内边框都存在
     */
    private allInnerBorderExist = (style) => {
        const inner = style.horizontal && style.vertical;
        return inner;
    };

    /**
     * 所有边框都不存在
     */
    private allBorderNotExist = (style) => {
        const none = !style.left && !style.right && !style.top && !style.bottom && !style.horizontal && !style.vertical;
        return none;
    };

    /**
     * 计算单元格边框样式
     */
    private caculateCellBorderStyle = (cellRange, borders, style) => {
        this.caculateCellTopBorderStyle(cellRange, borders, style);
        this.caculateCellBottomBorderStyle(cellRange, borders, style);
        this.caculateCellLeftBorderStyle(cellRange, borders, style);
        this.caculateCellRightBorderStyle(cellRange, borders, style);
    };

    private caculateCellTopBorderStyle = (cellRange, borders, style) => {
        if (borders) {
            const borderStyle = style;
            if (!borders.top || borders.top.noFill) {
                if (!this.topAdjacentCellSelected(cellRange.rowStart, cellRange.colStart)) {
                    borderStyle.top = false;
                } else {
                    borderStyle.horizontal = false;
                }
            }
        }
    };

    private caculateCellBottomBorderStyle = (cellRange, borders, style) => {
        if (borders) {
            const borderStyle = style;
            if (!borders.bottom || borders.bottom.noFill) {
                if (!this.bottomAdjacentCellSelected(cellRange.rowEnd, cellRange.colStart)) {
                    borderStyle.bottom = false;
                } else {
                    borderStyle.horizontal = false;
                }
            }
        }
    };

    private caculateCellLeftBorderStyle = (cellRange, borders, style) => {
        if (borders) {
            const borderStyle = style;
            if (!borders.left || borders.left.noFill) {
                if (!this.leftAdjacentCellSelected(cellRange.rowStart, cellRange.colStart)) {
                    borderStyle.left = false;
                } else {
                    borderStyle.vertical = false;
                }
            }
        }
    };

    private caculateCellRightBorderStyle = (cellRange, borders, style) => {
        if (borders) {
            const borderStyle = style;
            if (!borders.right || borders.right.noFill) {
                if (!this.rightAdjacentCellSelected(cellRange.rowStart, cellRange.colEnd)) {
                    borderStyle.right = false;
                } else {
                    borderStyle.vertical = false;
                }
            }
        }
    };

    /**
     * 获取选中单元格内部水平/垂直线的状态
     */
    private updateHVState = (style) => {
        // 只选中一个单元格时，无水平/垂直的样式，直接置为false
        if (this.tableModel.selectedCellRanges.length === 1) {
            style.horizontal = false;
            style.vertical = false;
        }
        // 只选中一行或者一列时，无相应水平/垂直线的样式，直接置为false
        const singleInfo = this.getSingleRowOrCol();
        if (singleInfo.rowStartIsSingle) {
            style.horizontal = false;
        }
        if (singleInfo.colStartIsSingle) {
            style.vertical = false;
        }
    };

    /**
    * 获取选中的单元格是否处于同一行或者同一列
    */
    private getSingleRowOrCol = () => {
        let rowStart;
        let colStart;
        this.tableModel.selectedCellRanges?.forEach?.((cell) => {
            if (isNaN(rowStart)) {
                rowStart = cell.rowStart;
            } else if (rowStart !== cell.rowStart) {
                rowStart = -1;
            }

            if (isNaN(colStart)) {
                colStart = cell.colStart;
            } else if (colStart !== cell.colStart) {
                colStart = -1;
            }
        });
        return {
            rowStartIsSingle: rowStart !== -1,
            colStartIsSingle: colStart !== -1,
        };
    };
    /** ***** 获取表格的颜色和边框样式 end ***** */

    /** ***** 更新表格边框样式(table-model.ts迁移而来) start ***** */
    /**
     * 更新表格边框样式
     */
    private modifyBorderStyle = (style: ITableBorderStyle): void => {
        const items: (ITableCellModelForEdit | undefined)[][] = [];
        this.tableModel.props.items.forEach((rowInfo, row) => {
            const rowBorders: (ITableCellModelForEdit | undefined)[] = [];
            rowInfo.forEach((shape, col) => {
                const selectedState = !shape.hMerge && !shape.vMerge
                    && this.tableModel.cellSelectedState(row, col, this.tableModel.selectedRange);
                if (selectedState) {
                    const borders = {};
                    const oldBorders = get(shape, 'borders') || {};
                    this.modifyCellTopBorderStyle(row, col, style, borders, oldBorders);
                    this.modifyCellBottomBorderStyle(row, col, style, borders, oldBorders);
                    this.modifyCellLeftBorderStyle(row, col, style, borders, oldBorders);
                    this.modifyCellRightBorderStyle(row, col, style, borders, oldBorders);
                    const cell = { borders };
                    rowBorders.push(cell as ITableCellModelForEdit);
                } else {
                    rowBorders.push(undefined);
                }
            });
            items.push(rowBorders);
        });

        for (let i = 0; i < this.tableModel.selectedCellRanges.length; i++) {
            const cellRange = this.tableModel.selectedCellRanges[i];
            this.modifyAdjacentBorderStyleForRange(style, cellRange, items);
        }
        this.tableUpdateProps(items);
    };

    private modifyCellTopBorderStyle = (
        row: number,
        col: number,
        style,
        borders: IBorders,
        oldBorders: IBorders,
    ): void => {
        const showBorder = !this.topAdjacentCellSelected(row, col)
            ? style.border_top
            : style.border_horizontal;
        if (this.borderStyleChanged(oldBorders.top, showBorder)) {
            if (showBorder) {
                const color = this.getRecentBorderColor();
                set(borders, 'top.color', color);
                set(borders, 'top.width', ptToCanvas(1));
                set(borders, 'top.tdColor', new TDColor({ rgbaStr: color }));
            } else {
                set(borders, 'top.noFill', 1);
            }
        }
    };
    private modifyCellBottomBorderStyle = (
        row: number,
        col: number,
        style,
        borders: IBorders,
        oldBorders: IBorders,
    ): void => {
        const showBorder = !this.bottomAdjacentCellSelected(row, col)
            ? style.border_bottom
            : style.border_horizontal;
        if (this.borderStyleChanged(oldBorders.bottom, showBorder)) {
            if (showBorder) {
                const color = this.getRecentBorderColor();
                set(borders, 'bottom.color', color);
                set(borders, 'bottom.width', ptToCanvas(1));
                set(borders, 'bottom.tdColor', new TDColor({ rgbaStr: color }));
            } else {
                set(borders, 'bottom.noFill', 1);
            }
        }
    };
    private modifyCellLeftBorderStyle = (
        row: number,
        col: number,
        style,
        borders: IBorders,
        oldBorders: IBorders,
    ): void => {
        const showBorder = !this.leftAdjacentCellSelected(row, col) ? style.border_left : style.border_vertical;
        if (this.borderStyleChanged(oldBorders.left, showBorder)) {
            if (showBorder) {
                const color = this.getRecentBorderColor();
                set(borders, 'left.color', color);
                set(borders, 'left.width', ptToCanvas(1));
                set(borders, 'left.tdColor', new TDColor({ rgbaStr: color }));
            } else {
                set(borders, 'left.noFill', 1);
            }
        }
    };
    private modifyCellRightBorderStyle = (
        row: number,
        col: number,
        style,
        borders: IBorders,
        oldBorders: IBorders,
    ): void => {
        const showBorder = !this.rightAdjacentCellSelected(row, col) ? style.border_right : style.border_vertical;
        if (this.borderStyleChanged(oldBorders.right, showBorder)) {
            if (showBorder) {
                const color = this.getRecentBorderColor();
                set(borders, 'right.color', color);
                set(borders, 'right.width', ptToCanvas(1));
                set(borders, 'right.tdColor', new TDColor({ rgbaStr: color }));
            } else {
                set(borders, 'right.noFill', 1);
            }
        }
    };

    private modifyAdjacentBorderStyleForRange(
        style,
        range: IRowColStartEnd,
        tempItems: (ITableCellModelForEdit | undefined)[][],
    ): void {
        for (let row = range.rowStart; row <= range.rowEnd; row++) {
            for (let col = range.colStart; col <= range.colEnd; col++) {
                // 顶部
                this.modifyAdjacentBorderStyleForRangeTop(style, range, tempItems, row, col);
                // 底部
                this.modifyAdjacentBorderStyleForRangeBottom(style, range, tempItems, row, col);
                // 左边
                this.modifyAdjacentBorderStyleForRangeLeft(style, range, tempItems, row, col);
                // 右边
                this.modifyAdjacentBorderStyleForRangeRight(style, range, tempItems, row, col);
            }
        }
    }

    private modifyAdjacentBorderStyleForRangeTop(
        style,
        range: IRowColStartEnd,
        tempItems: (ITableCellModelForEdit | undefined)[][],
        row: number,
        col: number,
    ): void {
        if (row === range.rowStart && row > 0 && !this.topAdjacentCellSelected(row, col)) {
            this.modifyTopAdjacentCellBorderStyle(row, col, style, tempItems);
        }
    }
    private modifyAdjacentBorderStyleForRangeBottom(
        style,
        range: IRowColStartEnd,
        tempItems: (ITableCellModelForEdit | undefined)[][],
        row: number,
        col: number,
    ): void {
        if (row === range.rowEnd && row < this.tableModel.props.rowHeights.length - 1
            && !this.bottomAdjacentCellSelected(row, col)) {
            this.modifyBottomAdjacentCellBorderStyle(row, col, style, tempItems);
        }
    }
    private modifyAdjacentBorderStyleForRangeLeft(
        style,
        range: IRowColStartEnd,
        tempItems: (ITableCellModelForEdit | undefined)[][],
        row: number,
        col: number,
    ): void {
        if (col === range.colStart && col > 0 && !this.leftAdjacentCellSelected(row, col)) {
            this.modifyLeftAdjacentCellBorderStyle(row, col, style, tempItems);
        }
    }
    private modifyAdjacentBorderStyleForRangeRight(
        style,
        range: IRowColStartEnd,
        tempItems: (ITableCellModelForEdit | undefined)[][],
        row: number,
        col: number,
    ): void {
        if (col === range.colEnd && col < this.tableModel.props.colWidths.length - 1
            && !this.rightAdjacentCellSelected(row, col)) {
            this.modifyRightAdjacentCellBorderStyle(row, col, style, tempItems);
        }
    }

    /**
     * 修改上方相邻的单元格边框（底边框）
     */
    private modifyTopAdjacentCellBorderStyle(
        row: number,
        col: number,
        style,
        tempItems: (ITableCellModelForEdit | undefined)[][],
    ): void {
        const items = tempItems;
        const mergedCellRange = this.tableModel.getMergedCellRangeForCoords(row - 1, col);
        const adjacentRow = mergedCellRange.rowStart;
        const adjacentCol = mergedCellRange.colStart;
        const adjacentShape = this.tableModel.props.items[adjacentRow][adjacentCol];
        if (adjacentCol >= col) {
            const itemBorderInfo = items[adjacentRow][adjacentCol] || { borders: {} };
            const oldBorder = get(adjacentShape, 'borders.bottom') || {};
            const border = { ...oldBorder };
            const showBorder = style.border_top;
            if (this.borderStyleChanged(oldBorder, showBorder)) {
                if (showBorder) {
                    const color = this.getRecentBorderColor();
                    set(border, 'color', color);
                    set(border, 'tdColor', new TDColor({ rgbaStr: color }));
                    if (!border.width) {
                        border.width = ptToCanvas(1);
                    }
                } else {
                    set(border, 'noFill', 1);
                }
                set(itemBorderInfo, 'borders.bottom', border);
                items[adjacentRow][adjacentCol] = itemBorderInfo as ITableCellModelForEdit;
            }
        }
    }

    /**
     * 修改下方相邻的单元格边框（上边框）
     */
    private modifyBottomAdjacentCellBorderStyle(
        row: number,
        col: number,
        style,
        tempItems: (ITableCellModelForEdit | undefined)[][],
    ): void {
        const items = tempItems;
        const mergedCellRange = this.tableModel.getMergedCellRangeForCoords(row + 1, col);
        const adjacentRow = mergedCellRange.rowStart;
        const adjacentCol = mergedCellRange.colStart;
        const adjacentShape = this.tableModel.props.items[adjacentRow][adjacentCol];
        if (adjacentCol >= col) {
            const itemBorderInfo = items[adjacentRow][adjacentCol] || { borders: {} };
            const oldBorder = get(adjacentShape, 'borders.top') || {};
            const border = { ...oldBorder };
            const showBorder = style.border_bottom;
            if (this.borderStyleChanged(oldBorder, showBorder)) {
                if (showBorder) {
                    const color = this.getRecentBorderColor();
                    set(border, 'color', color);
                    set(border, 'tdColor', new TDColor({ rgbaStr: color }));
                    if (!border.width) {
                        border.width = ptToCanvas(1);
                    }
                } else {
                    set(border, 'noFill', 1);
                }
                set(itemBorderInfo, 'borders.top', border);
                items[adjacentRow][adjacentCol] = itemBorderInfo;
            }
        }
    }

    /**
     * 修改左方相邻的单元格边框（右边框）
     */
    private modifyLeftAdjacentCellBorderStyle(
        row: number,
        col: number,
        style,
        tempItems: (ITableCellModelForEdit | undefined)[][],
    ) {
        const items = tempItems;
        const mergedCellRange = this.tableModel.getMergedCellRangeForCoords(row, col - 1);
        const adjacentRow = mergedCellRange.rowStart;
        const adjacentCol = mergedCellRange.colStart;
        const adjacentShape = this.tableModel.props.items[adjacentRow][adjacentCol];
        if (adjacentRow >= row) {
            const itemBorderInfo = items[adjacentRow][adjacentCol] || { borders: {} };
            const oldBorder = get(adjacentShape, 'borders.right') || {};
            const border = { ...oldBorder };
            const showBorder = style.border_left;
            if (this.borderStyleChanged(oldBorder, showBorder)) {
                if (showBorder) {
                    const color = this.getRecentBorderColor();
                    set(border, 'color', color);
                    set(border, 'tdColor', new TDColor({ rgbaStr: color }));
                    if (!border.width) {
                        border.width = ptToCanvas(1);
                    }
                } else {
                    set(border, 'noFill', 1);
                }
                set(itemBorderInfo, 'borders.right', border);
                items[adjacentRow][adjacentCol] = itemBorderInfo;
            }
        }
    }

    /**
     * 修改右方相邻的单元格边框（左边框）
     */
    private modifyRightAdjacentCellBorderStyle(
        row: number,
        col: number,
        style,
        tempItems: (ITableCellModelForEdit | undefined)[][],
    ) {
        const items = tempItems;
        const mergedCellRange = this.tableModel.getMergedCellRangeForCoords(row, col + 1);
        const adjacentRow = mergedCellRange.rowStart;
        const adjacentCol = mergedCellRange.colStart;
        const adjacentShape = this.tableModel.props.items[adjacentRow][adjacentCol];
        if (adjacentRow >= row) {
            const itemBorderInfo = items[adjacentRow][adjacentCol] || { borders: {} };
            const oldBorder = get(adjacentShape, 'borders.left') || {};
            const border = { ...oldBorder };
            const showBorder = style.border_right;
            if (this.borderStyleChanged(oldBorder, showBorder)) {
                if (showBorder) {
                    const color = this.getRecentBorderColor();
                    set(border, 'color', color);
                    set(border, 'tdColor', new TDColor({ rgbaStr: color }));
                    if (!border.width) {
                        border.width = ptToCanvas(1);
                    }
                } else {
                    set(border, 'noFill', 1);
                }
                set(itemBorderInfo, 'borders.left', border);
                items[adjacentRow][adjacentCol] = itemBorderInfo;
            }
        }
    }

    private borderStyleChanged = (borderInfo, showBorder) => {
        const noFill = !!(!borderInfo || Object.keys(borderInfo).length <= 0 || (borderInfo?.noFill));
        return !(noFill === !showBorder);
    };

    private getRecentBorderColor(): string {
        let color = userLocalStorage.getItem(LocalStorageKeys.BORDER_COLOR_RECENT_COLOR);
        if (!color || color === NO_COLOR) {
            color = '#999999';
        }
        return color;
    }
    /** ***** 更新表格边框样式 end ***** */

    /** ***** 更新表格边框颜色(table-model.ts迁移而来) start ***** */
    /**
     *
     * 之前是更新表格颜色的接口，现修改为更新表格边框的统一接口，没必要独立处理每一个属性
     * 包括颜色、粗细、边框类型
     */
    private modifyBorderInfo = (colorInfo: IBorderInfo): void => {
        const tableModel = this.tableModel as ITableModel;
        let borderColor: ITDColor | null;
        let borderWidth;
        let borderStyle;
        if (colorInfo) {
            const { tdColor, width, style } = colorInfo;
            if (tdColor !== undefined) {
                borderColor = tdColor;
            }

            if (width !== undefined) {
                borderWidth = width;
            }
            if (style !== undefined) {
                borderStyle = style;
            }
        }

        const items: (ITableCellModelForEdit | undefined)[][] = [];
        const defaultBorderWidth = ptToCanvas(1);

        tableModel.props.items.forEach((rowInfo, row) => {
            const rowBorders: (ITableCellModelForEdit | undefined)[] = [];
            rowInfo.forEach((shape: ITableCellModel, col) => {
                // 非合并的单元格才处理数据
                const selectedState = !shape.hMerge && !shape.vMerge
                    && tableModel.cellSelectedState(row, col, tableModel.selectedRange!);
                if (selectedState) {
                    const cellModel = this.tableModel.props.items?.[row]?.[col] as ITableCellModel;
                    const { left, top, bottom, right } = cellModel?.borders || {};
                    const borders = {
                        left: { ...left?.border },
                        top: { ...top?.border },
                        right: { ...right?.border },
                        bottom: { ...bottom?.border },
                    };
                    if (borderColor) {
                        set(borders, 'top.color', borderColor.srgbColor);
                        set(borders, 'left.color', borderColor.srgbColor);
                        set(borders, 'bottom.color', borderColor.srgbColor);
                        set(borders, 'right.color', borderColor.srgbColor);
                        set(borders, 'top.tdColor', borderColor);
                        set(borders, 'left.tdColor', borderColor);
                        set(borders, 'bottom.tdColor', borderColor);
                        set(borders, 'right.tdColor', borderColor);
                        set(borders, 'top.width', nullToDefault(nullToDefault(borderWidth, get(shape.borders, 'top.border.width')), defaultBorderWidth));
                        set(borders, 'left.width', nullToDefault(nullToDefault(borderWidth, get(shape.borders, 'left.border.width')), defaultBorderWidth));
                        set(borders, 'bottom.width', nullToDefault(nullToDefault(borderWidth, get(shape.borders, 'bottom.border.width')), defaultBorderWidth));
                        set(borders, 'right.width', nullToDefault(nullToDefault(borderWidth, get(shape.borders, 'right.border.width')), defaultBorderWidth));
                    }

                    if (borderWidth) {
                        set(borders, 'top.width', nullToDefault(nullToDefault(borderWidth, get(shape.borders, 'top.border.width')), defaultBorderWidth));
                        set(borders, 'left.width', nullToDefault(nullToDefault(borderWidth, get(shape.borders, 'left.border.width')), defaultBorderWidth));
                        set(borders, 'bottom.width', nullToDefault(nullToDefault(borderWidth, get(shape.borders, 'bottom.border.width')), defaultBorderWidth));
                        set(borders, 'right.width', nullToDefault(nullToDefault(borderWidth, get(shape.borders, 'right.border.width')), defaultBorderWidth));
                    }

                    if (borderStyle) {
                        set(borders, 'top.style', borderStyle);
                        set(borders, 'left.style', borderStyle);
                        set(borders, 'bottom.style', borderStyle);
                        set(borders, 'right.style', borderStyle);
                    }

                    if (borderColor === null) {
                        set(borders, 'top.noFill', 1);
                        set(borders, 'left.noFill', 1);
                        set(borders, 'bottom.noFill', 1);
                        set(borders, 'right.noFill', 1);
                    }

                    // @ts-ignore
                    rowBorders.push({ borders });
                } else {
                    rowBorders.push(undefined);
                }
            });
            items.push(rowBorders);
        });

        tableModel.selectedCellRanges.forEach((cellRange) => {
            this.modifyAdjacentBorderColorForRange(colorInfo, borderWidth, cellRange, items);
        });
        this.tableUpdateProps(items);
    };

    /**
     * 更新表格选区边界的边框颜色
     */
    private modifyAdjacentBorderColorForRange(
        borderInfo: IBorderInfo,
        width: number,
        range: IRowColStartEnd,
        tempItems: (ITableCellModelForEdit | undefined)[][],
    ): void {
        for (let row = range.rowStart; row <= range.rowEnd; row++) {
            for (let col = range.colStart; col <= range.colEnd; col++) {
                // 顶部
                this.modifyAdjacentBorderColorForRangeTop(borderInfo, width, range, tempItems, row, col);
                // 底部
                this.modifyAdjacentBorderColorForRangeBottom(borderInfo, width, range, tempItems, row, col);
                // 左边
                this.modifyAdjacentBorderColorForRangeLeft(borderInfo, width, range, tempItems, row, col);
                // 右边
                this.modifyAdjacentBorderColorForRangeRight(borderInfo, width, range, tempItems, row, col);
            }
        }
    }

    private modifyAdjacentBorderColorForRangeTop(
        borderInfo: IBorderInfo,
        width: number,
        range: IRowColStartEnd,
        tempItems: (ITableCellModelForEdit | undefined)[][],
        row: number,
        col: number,
    ): void {
        if (row === range.rowStart && row > 0 && !this.topAdjacentCellSelected(row, col)) {
            this.modifyTopAdjacentCellBorderColor(row, col, borderInfo, width, tempItems);
        }
    }
    private modifyAdjacentBorderColorForRangeBottom(
        borderInfo: IBorderInfo,
        width: number,
        range: IRowColStartEnd,
        tempItems: (ITableCellModelForEdit | undefined)[][],
        row: number,
        col: number,
    ): void {
        if (row === range.rowEnd && row < this.tableModel.props.rowHeights.length - 1
            && !this.bottomAdjacentCellSelected(row, col)) {
            this.modifyBottomAdjacentCellBorderColor(row, col, borderInfo, width, tempItems);
        }
    }
    private modifyAdjacentBorderColorForRangeLeft(
        borderInfo: IBorderInfo,
        width: number,
        range: IRowColStartEnd,
        tempItems: (ITableCellModelForEdit | undefined)[][],
        row: number,
        col: number,
    ): void {
        if (col === range.colStart && col > 0 && !this.leftAdjacentCellSelected(row, col)) {
            this.modifyLeftAdjacentCellBorderColor(row, col, borderInfo, width, tempItems);
        }
    }
    private modifyAdjacentBorderColorForRangeRight(
        borderInfo: IBorderInfo,
        width: number,
        range: IRowColStartEnd,
        tempItems: (ITableCellModelForEdit | undefined)[][],
        row: number,
        col: number,
    ): void {
        if (col === range.colEnd && col < this.tableModel.props.colWidths.length - 1
            && !this.rightAdjacentCellSelected(row, col)) {
            this.modifyRightAdjacentCellBorderColor(row, col, borderInfo, width, tempItems);
        }
    }

    /**
     * 修改上方相邻的单元格边框颜色（下边框）
     */
    private modifyTopAdjacentCellBorderColor(
        row: number,
        col: number,
        borderInfo: IBorderInfo,
        borderWidth: number,
        tempItems: (ITableCellModelForEdit | undefined)[][],
    ): void {
        const items = tempItems;
        const defaultBorderWidth = ptToCanvas(1);
        const item = items[row][col];
        const mergedCellRange = this.tableModel.getMergedCellRangeForCoords(row - 1, col);
        const adjacentRow = mergedCellRange.rowStart;
        const adjacentCol = mergedCellRange.colStart;
        if (adjacentCol >= col) {
            const itemBorderInfo = nullToDefault(items[adjacentRow][adjacentCol], { borders: {} });
            const shape = this.tableModel.props.items[adjacentRow][adjacentCol];
            const border: IBorderInfo = {};
            const { tdColor, width, style } = item?.borders?.top || {};
            if (tdColor) {
                set(border, 'color', tdColor.srgbColor);
                set(border, 'tdColor', tdColor);
            }

            if (width) {
                set(border, 'width', nullToDefault(nullToDefault(width, get(shape.borders, 'bottom.border.width')), defaultBorderWidth));
            }

            if (style) {
                set(border, 'style', style);
            }

            if (tdColor === null) {
                set(border, 'noFill', 1);
            }

            set(itemBorderInfo, 'borders.bottom', border);
            items[adjacentRow][adjacentCol] = itemBorderInfo;
        }
    }

    /**
     * 修改下方相邻的单元格边框颜色（上边框）
     */
    private modifyBottomAdjacentCellBorderColor(
        row: number,
        col: number,
        borderInfo: IBorderInfo,
        borderWidth: number,
        tempItems: (ITableCellModelForEdit | undefined)[][],
    ): void {
        const items = tempItems;
        const defaultBorderWidth = ptToCanvas(1);
        const mergedCellRange = this.tableModel.getMergedCellRangeForCoords(row + 1, col);
        const adjacentRow = mergedCellRange.rowStart;
        const adjacentCol = mergedCellRange.colStart;
        if (adjacentCol >= col) {
            const itemBorderInfo = nullToDefault(items[adjacentRow][adjacentCol], { borders: {} });
            const shape = this.tableModel.props.items[adjacentRow][adjacentCol];
            const border: IBorderInfo = {};
            const item = items[row][col];
            const { tdColor, width, style } = item?.borders?.bottom || {};
            if (tdColor) {
                set(border, 'color', tdColor.srgbColor);
                set(border, 'tdColor', tdColor);
            }

            if (width) {
                set(border, 'width', nullToDefault(nullToDefault(width, get(shape.borders, 'bottom.border.width')), defaultBorderWidth));
            }

            if (tdColor === null) {
                set(border, 'noFill', 1);
            }

            if (style) {
                set(border, 'style', style);
            }

            set(itemBorderInfo, 'borders.top', border);
            items[adjacentRow][adjacentCol] = itemBorderInfo;
        }
    }

    /**
     * 修改左方相邻的单元格边框颜色（右边框）
     */
    private modifyLeftAdjacentCellBorderColor(
        row: number,
        col: number,
        borderInfo: IBorderInfo,
        borderWidth: number,
        tempItems: (ITableCellModelForEdit | undefined)[][],
    ): void {
        const items = tempItems;
        const defaultBorderWidth = ptToCanvas(1);
        const mergedCellRange = this.tableModel.getMergedCellRangeForCoords(row, col - 1);
        const adjacentRow = mergedCellRange.rowStart;
        const adjacentCol = mergedCellRange.colStart;
        if (adjacentRow >= row) {
            const itemBorderInfo = nullToDefault(items[adjacentRow][adjacentCol], { borders: {} });
            const shape = this.tableModel.props.items[adjacentRow][adjacentCol];
            const border: IBorderInfo = {};
            const item = items[row][col];
            const { tdColor, width, style } = item?.borders?.left || {};
            if (tdColor) {
                set(border, 'color', tdColor.srgbColor);
                set(border, 'tdColor', tdColor);
            }

            if (width) {
                set(border, 'width', nullToDefault(nullToDefault(width, get(shape.borders, 'bottom.border.width')), defaultBorderWidth));
            }

            if (tdColor === null) {
                set(border, 'noFill', 1);
            }

            if (style) {
                set(border, 'style', style);
            }

            set(itemBorderInfo, 'borders.right', border);
            items[adjacentRow][adjacentCol] = itemBorderInfo;
        }
    }

    /**
     * 修改右方相邻的单元格边框颜色（左边框）
     */
    private modifyRightAdjacentCellBorderColor(
        row: number,
        col: number,
        borderInfo: IBorderInfo,
        borderWidth: number,
        tempItems: (ITableCellModelForEdit | undefined)[][],
    ): void {
        const items = tempItems;
        const defaultBorderWidth = ptToCanvas(1);
        const mergedCellRange = this.tableModel.getMergedCellRangeForCoords(row, col + 1);
        const adjacentRow = mergedCellRange.rowStart;
        const adjacentCol = mergedCellRange.colStart;
        if (adjacentRow >= row) {
            const itemBorderInfo = nullToDefault(items[adjacentRow][adjacentCol], { borders: {} });
            const shape = this.tableModel.props.items[adjacentRow][adjacentCol];
            const border: IBorderInfo = {};
            const item = items[row][col];
            const { tdColor, width, style } = item?.borders?.right || {};
            if (tdColor) {
                set(border, 'color', tdColor.srgbColor);
                set(border, 'tdColor', tdColor);
            }

            if (width) {
                set(border, 'width', nullToDefault(nullToDefault(width, get(shape.borders, 'bottom.border.width')), defaultBorderWidth));
            }

            if (tdColor === null) {
                set(border, 'noFill', 1);
            }

            if (style) {
                set(border, 'style', style);
            }

            set(itemBorderInfo, 'borders.left', border);
            items[adjacentRow][adjacentCol] = itemBorderInfo;
        }
    }


    private topAdjacentCellSelected = (row: number, col: number): boolean => {
        if (row > 0) {
            const adjacentRow = row - 1;
            const adjacentCol = col;
            return this.tableModel.cellCoordsInSelectedRanges(
                adjacentRow, adjacentCol,
                this.tableModel.selectedCellRanges,
            );
        }
        return false;
    };

    // 当前单元格的下方紧邻单元格是否在选中区域内
    private bottomAdjacentCellSelected = (row: number, col: number): boolean => {
        if (row < this.tableModel.props.rowHeights.length - 1) {
            let adjacentRow = row + 1;
            // 如果当前单元格是合并单元格，则下方紧邻的单元格应当是指合并后单元格的下方单元格，而不是合并单元格的一个子单元格
            while (this.tableModel.props.items?.[adjacentRow]?.[col]?.vMerge) {
                adjacentRow = adjacentRow + 1;
            }
            const adjacentCol = col;
            return this.tableModel.cellCoordsInSelectedRanges(
                adjacentRow, adjacentCol,
                this.tableModel.selectedCellRanges,
            );
        }
        return false;
    };

    private leftAdjacentCellSelected = (row: number, col: number): boolean => {
        if (col > 0) {
            const adjacentRow = row;
            const adjacentCol = col - 1;
            return this.tableModel.cellCoordsInSelectedRanges(
                adjacentRow, adjacentCol,
                this.tableModel.selectedCellRanges,
            );
        }
        return false;
    };

    // 当前单元格的右侧紧邻单元格是否在选中区域内
    private rightAdjacentCellSelected = (row: number, col: number): boolean => {
        if (col < this.tableModel.props.colWidths.length - 1) {
            const adjacentRow = row;
            let adjacentCol = col + 1;
            // 如果当前单元格是合并单元格，则右侧紧邻的单元格应当是指合并后单元格的右侧单元格，而不是合并单元格的一个子单元格
            while (this.tableModel.props.items?.[row]?.[adjacentCol]?.hMerge) {
                adjacentCol = adjacentCol + 1;
            }
            return this.tableModel.cellCoordsInSelectedRanges(
                adjacentRow, adjacentCol,
                this.tableModel.selectedCellRanges,
            );
        }
        return false;
    };
    /** ***** 更新表格边框颜色 end ***** */
    private tableUpdateProps(items) {
        const sdkDataV3 = this.tableModel.sdkData as unknown as ITableDataModel;
        sdkDataV3.api.transaction(() => {
            items.forEach((row, rowIndex) => {
                row.forEach((col, colIndex) => {
                    if (!col) {
                        return;
                    }
                    const sdkCell: ICTTableCell = {};
                    TableCellPropertyConverter.convertPartialDataFromUiToSdk(col, sdkCell);
                    const range = { startRowIndex: rowIndex, startColIndex: colIndex, rowSpan: 1, colSpan: 1 };
                    if (sdkCell.tcPr) {
                        changeArrAppendToSetOrModify(sdkCell.tcPr);
                        sdkDataV3.api.updateTableCellProperties(sdkCell.tcPr, range);
                    }
                });
            });
        });
    }
}
